import { useId, useState, type ReactNode } from "react";

import { cx } from "@/lib/utils";

export interface AccordionItem {
  id?: string;
  title: ReactNode;
  body: ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  initialOpenIndex?: number | null;
  className?: string;
  testId?: string;
  marker?: "chevron" | "plus-minus";
}

export function Accordion({
  items,
  initialOpenIndex = null,
  className,
  testId,
  marker = "chevron",
}: AccordionProps): JSX.Element {
  const [openIndex, setOpenIndex] = useState<number | null>(initialOpenIndex);
  const baseId = useId();

  return (
    <div
      className={cx("divide-y divide-border rounded-md border border-border bg-surface", className)}
      data-testid={testId}
    >
      {items.map((it, i) => {
        const isOpen = i === openIndex;
        const panelId = `${baseId}-panel-${i}`;
        const buttonId = `${baseId}-btn-${i}`;
        return (
          <div key={it.id ?? i}>
            <h3 className="m-0">
              <button
                type="button"
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-[15px] font-medium text-ink hover:bg-surface-2"
              >
                <span>{it.title}</span>
                {marker === "plus-minus" ? (
                  <span
                    aria-hidden="true"
                    className="mono text-lg leading-none text-ink-3"
                  >
                    {isOpen ? "−" : "+"}
                  </span>
                ) : (
                  <span
                    aria-hidden="true"
                    className={cx(
                      "inline-block h-2 w-2 border-r border-b border-ink-3 transition-transform",
                      isOpen ? "rotate-[225deg]" : "rotate-45",
                    )}
                  />
                )}
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
              className="px-5 pb-5 pt-0 text-sm text-ink-2"
            >
              {it.body}
            </div>
          </div>
        );
      })}
    </div>
  );
}
